"use client";

/**
 * EquitySummaryWidget — primary portfolio value + day P&L.
 * Reusable on client dashboard, portfolio page, agent desks.
 */
import React from "react";
import { Skeleton } from "antd";
import { RiseOutlined, FallOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { SensitiveDataMask } from "@/shared/components/security/SensitiveDataMask";
import { formatCurrency } from "@/shared/utils/formatters";
import {
  formatSignedCurrency,
  formatSignedPercent,
} from "./lib/deskFormat";
import type { EquitySummaryValues } from "./lib/types";
import { WidgetChrome } from "./WidgetChrome";
import styles from "./WidgetChrome.module.css";
import local from "./EquitySummaryWidget.module.css";

export interface EquitySummaryWidgetProps {
  values: EquitySummaryValues;
  loading?: boolean;
  label?: string;
  meta?: string;
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  className?: string;
  /** Mask sensitive balances (default true) */
  maskValues?: boolean;
}

export function EquitySummaryWidget({
  values,
  loading = false,
  label = "Equity",
  meta = "Market value",
  primaryAction,
  secondaryAction,
  className,
  maskValues = true,
}: EquitySummaryWidgetProps) {
  const { portfolioValue, dayPnl, dayReturnPct } = values;
  const pnlPositive = (dayPnl ?? 0) >= 0;

  const valueNode =
    portfolioValue != null ? (
      maskValues ? (
        <SensitiveDataMask value={formatCurrency(portfolioValue)} />
      ) : (
        formatCurrency(portfolioValue)
      )
    ) : (
      "—"
    );

  return (
    <WidgetChrome
      label={label}
      meta={meta}
      emphasis="primary"
      aria-label="Portfolio equity"
      className={className}
    >
      <div className={local.layout}>
        <div className={local.primary}>
          <p className={local.fieldLabel}>Portfolio value</p>
          {loading ? (
            <Skeleton active paragraph={{ rows: 1 }} title={false} />
          ) : (
            <p
              className={`${styles.value} ${portfolioValue == null ? styles.valueEmpty : ""
                } ${local.heroValue}`}
            >
              {valueNode}
            </p>
          )}
          <div className={local.deltaRow}>
            {loading ? null : dayPnl != null ? (
              <>
                <span
                  className={pnlPositive ? styles.deltaUp : styles.deltaDown}
                >
                  {pnlPositive ? (
                    <RiseOutlined aria-hidden />
                  ) : (
                    <FallOutlined aria-hidden />
                  )}
                  {formatSignedCurrency(dayPnl)}
                </span>
                <span className={local.sep} aria-hidden>
                  ·
                </span>
                {dayReturnPct != null ? (
                  <span
                    className={
                      dayReturnPct >= 0 ? styles.deltaUp : styles.deltaDown
                    }
                  >
                    {formatSignedPercent(dayReturnPct)} today
                  </span>
                ) : (
                  <span className={styles.pending}>Return pending</span>
                )}
              </>
            ) : (
              <span className={styles.pending}>
                {portfolioValue != null
                  ? "Day change pending"
                  : "Waiting for live data"}
              </span>
            )}
          </div>
        </div>
        {(primaryAction || secondaryAction) && (
          <div className={local.actions}>
            {primaryAction ? (
              <button
                type="button"
                className={styles.cta}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label} <ArrowRightOutlined aria-hidden />
              </button>
            ) : null}
            {secondaryAction ? (
              <button
                type="button"
                className={styles.ctaGhost}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </WidgetChrome>
  );
}
